import debug from "debug";
import { DragResizeModal } from "./Modals/DragResizeModal";
const log = debug("app:fn");

/**
 * Class to manage the S-Parameter modal.
 * @category Components
 * @subcategory ContextMenu
 */
class SParamModal {
  /**
   * Creates an instance of SParamModal.
   * @param {Object} experience - The experience object containing the event hub.
   */
  constructor(experience) {
    /**
     * Event hub for handling events.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * The modal DOM element.
     * @type {HTMLElement|null}
     */
    this.modal = null;

    // Listen for event to show the modal
    this.eventHub.on("showSParameterModal", this.showModal.bind(this));
  }


  cleanup() {
    console.log("SParamModal cleanup started...");

    // 1. Remove event listeners from eventHub
    if (this.handleShowSParamModal) {
      this.eventHub.off("showSParameterModal", this.handleShowSParamModal);
    }
  
    // 2. Remove document event listeners (outside click, drag listeners)
    if (this.outsideClickListener) {
      document.removeEventListener("click", this.outsideClickListener);
    }
  
    // 3. Remove modal from DOM
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
    }
  
    // 4. Clear object references
    this.modal = null;
    this.outsideClickListener = null;
    this.eventHub = null;
  
    console.log("SParamModal cleanup completed.");
  }

  /**
   * Displays the modal with the given data URL.
   * @param {string} dataURL - The URL to display in the modal's iframe.
   */
  showModal(dataURL) {
    // Dispose of any existing modal
    if (this.modal) this.dispose();

    // Create the modal structure
    const modalContainer = this.createModalContainer();
    const header = this.createHeader();
    const sIframe = this.createsIframe(dataURL);
    const footer = this.createFooter();

    // Wrap header and body in a draggable container
    const draggableContainer = document.createElement("div");
    draggableContainer.style.flex = "1"; // Allow the container to fill space
    draggableContainer.appendChild(header);
    draggableContainer.appendChild(sIframe);

    // Append header and sIframe to modal
    modalContainer.appendChild(draggableContainer);
    modalContainer.appendChild(footer);

    // Append modal to the document body
    document.body.appendChild(modalContainer);

    this.modal = modalContainer;

    // Log dimensions to ensure proper sizing
    console.log(
      "Modal dimensions:",
      modalContainer.offsetWidth,
      modalContainer.offsetHeight
    );
    console.log(
      "sIframe dimensions:",
      sIframe.offsetWidth,
      sIframe.offsetHeight
    );

    // Add event listener for outside click
    this.outsideClickListener = (event) => {
      if (this.modal && !this.modal.contains(event.target)) {
        this.dispose(); // Close the modal
      }
    };
    document.addEventListener("click", this.outsideClickListener);

    // Enable drag and resize using DragResizeModal
    this.enableDragging(draggableContainer, modalContainer);
  }

  /**
   * Creates the container element for the modal.
   * @returns {HTMLElement} - The modal container element.
   */
  createModalContainer() {
    const modalContainer = document.createElement("div");

    // Ensure the modal container fully encapsulates its children
    modalContainer.style.position = "fixed";
    modalContainer.style.top = "20%"; // Centered vertically
    modalContainer.style.left = "10%"; // Centered horizontally
    modalContainer.style.width = "70%"; // 70% of viewport width
    modalContainer.style.height = "50%"; // Maintain aspect ratio based on viewport width
    //modalContainer.style.transform = "translate(-50%, -50%)"; // Offset by half its width/height to center it
    modalContainer.style.padding = "10"; // Remove padding
    modalContainer.style.margin = "0"; // Remove margins
    modalContainer.style.border = "1px solid #ccc"; // Add a light gray border
    modalContainer.style.borderRadius = "8px"; // Rounded corners
    modalContainer.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)"; // Shadow for depth
    modalContainer.style.boxSizing = "border-box"; // Include borders/padding in size
    modalContainer.style.overflow = "hidden"; // Ensure no scrollbars appear
    modalContainer.style.display = "flex";
    modalContainer.style.flexDirection = "column"; // Flex layout for proper sIframe alignment
    modalContainer.style.backgroundColor = "rgba(255, 255, 255, 0.95)"; // Less transparent background
    modalContainer.style.resize = "both";
    modalContainer.style.zIndex = "1000";
    modalContainer.style.minWidth = "200px"; // Optional: prevent collapsing
    modalContainer.style.minHeight = "150px"; // Optional: prevent collapsing
    modalContainer.style.transformOrigin = "top left";

    const computedStyle = window.getComputedStyle(modalContainer);
    const left = computedStyle.left; // Actual computed value
    const top = computedStyle.top; // Actual computed value

    setTimeout(() => {
      const rect = modalContainer.getBoundingClientRect(); // Get resolved position
      console.log("left:", rect.left, "top:", rect.top);
    }, 200); // Delay to ensure styles are applied
    return modalContainer;
  }

  /**
   * Creates the header for the modal.
   * @returns {HTMLElement} - The header element.
   */
  createHeader() {
    const header = document.createElement("div");

    // Header styling to ensure it doesn't interfere with sIframe size
    header.style.display = "flex";
    header.style.justifyContent = "flex-end";
    header.style.alignItems = "center";
    header.style.padding = "2px";
    header.style.backgroundColor = "rgba(255,255,255,0.9)";
    header.style.borderBottom = "1px solid #ddd";
    header.style.flex = "0 0 auto"; // Header doesn't grow/shrink
    header.style.boxSizing = "border-box"; // Include padding in its size

    const closeButton = document.createElement("button");
    closeButton.textContent = "✖";
    closeButton.style.background = "none";
    closeButton.style.border = "none";
    closeButton.style.fontSize = "18px";
    closeButton.style.cursor = "pointer";
    closeButton.style.color = "#000";

    closeButton.addEventListener("click", () => this.dispose());

    header.appendChild(closeButton);
    return header;
  }

  /**
   * Creates the iframe to display content in the modal.
   * @param {string} dataURL - The URL to load in the iframe.
   * @returns {HTMLElement} - The iframe element.
   */
  createsIframe(dataURL) {
    const sIframe = document.createElement("iframe");

    // The sIframe adjusts perfectly to fit its parent container
    sIframe.src = dataURL;
    sIframe.style.width = "100%";
    sIframe.style.height = "100%";
    sIframe.style.border = "none";
    sIframe.style.display = "block"; // Ensure sIframe acts as a block-level element
    return sIframe;
  }

  /**
   * Creates the footer for the modal.
   * @returns {HTMLElement} - The footer element.
   */
  createFooter() {
    const footer = document.createElement("div");
    footer.style.flex = "0 0 15px"; // Fixed height
    footer.style.backgroundColor = "rgba(255,255,255,0.9)";
    footer.style.borderTop = "1px solid #ddd";
    footer.style.cursor = "default"; // Not draggable
    return footer;
  }

  /**
   * Enables dragging functionality for the modal.
   * @param {HTMLElement} draggableHandle - The element to drag.
   * @param {HTMLElement} draggableContainer - The modal container element.
   */
  enableDragging(draggableHandle, draggableContainer) {
    let isDragging = false;
    let pointerStartX, pointerStartY, modalStartX, modalStartY;

    draggableHandle.style.cursor = "grab";

    draggableHandle.addEventListener("mousedown", (e) => {
      e.preventDefault();
      isDragging = true;

      draggableHandle.style.cursor = "grabbing";

      // Get the current position without overriding resize behavior
      const rect = draggableContainer.getBoundingClientRect();
      pointerStartX = e.clientX;
      pointerStartY = e.clientY;

      modalStartX = rect.left;
      modalStartY = rect.top;

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });

    const onMouseMove = (e) => {
      if (!isDragging) return;

      // Update modal position while maintaining its current size
      const deltaX = pointerStartX - e.clientX;
      const deltaY = pointerStartY - e.clientY;

      draggableContainer.style.left = `${modalStartX - deltaX}px`;
      draggableContainer.style.top = `${modalStartY - deltaY}px`;
      draggableContainer.style.position = "fixed"; // Ensure position stays fixed
    };

    const onMouseUp = () => {
      isDragging = false;
      draggableHandle.style.cursor = "grab";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }

  /**
   * Disposes of the modal and removes related event listeners.
   */
  dispose() {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
    if (this.outsideClickListener) {
      document.removeEventListener("click", this.outsideClickListener);
      this.outsideClickListener = null;
    }
  }
}

export default SParamModal;
